import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { deleteTitle, getTitles, getTitleTree } from '../../../../api/endpoints/corporate';
import type { Title, TitleTreeNode } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { TitleFormDialog } from '../TitleFormDialog';
import { TitleReparentDialog } from '../TitleReparentDialog';
import { TitleOrgChartView } from '../OrgChartView';

/** Flattens the parent-linked list into a depth-annotated, DFS-ordered array so it can be
 *  rendered as an indented plain Table — see DepartmentListPage's identical helper for the
 *  DataGrid-incompatibility + orphan-row rationale (duplicated here rather than shared, to
 *  keep the department/title module boundaries independent). */
function flattenTree(rows: Title[]): { row: Title; depth: number }[] {
  const byParent = new Map<number | null, Title[]>();
  for (const row of rows) {
    const key = row.parentTitleId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row);
  }
  const result: { row: Title; depth: number }[] = [];
  const visited = new Set<number>();

  function walk(parentId: number | null, depth: number) {
    const children = (byParent.get(parentId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      result.push({ row: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);

  for (const row of rows) {
    if (!visited.has(row.id)) {
      visited.add(row.id);
      result.push({ row, depth: 0 });
    }
  }
  return result;
}

function collectDescendantIds(rows: Title[], rootId: number): Set<number> {
  const byParent = new Map<number | null, number[]>();
  for (const row of rows) {
    const key = row.parentTitleId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row.id);
  }
  const result = new Set<number>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop() as number;
    for (const childId of byParent.get(id) ?? []) {
      if (!result.has(childId)) {
        result.add(childId);
        stack.push(childId);
      }
    }
  }
  return result;
}

/** Flatten `CorporateTitle/GetTree` back into the parent-linked `Title[]` shape the rest of
 *  this page consumes — synthesizing `parentTitleId` from the nesting. */
function treeToRows(nodes: TitleTreeNode[], parentId: number | null = null): Title[] {
  const out: Title[] = [];
  for (const node of nodes) {
    out.push({
      id: node.id,
      name: node.name,
      isActive: node.isActive,
      canViewSubordinates: node.canViewSubordinates,
      level: node.level,
      parentTitleId: parentId,
    });
    if (node.children?.length) out.push(...treeToRows(node.children, node.id));
  }
  return out;
}

export default function TitleListPage() {
  const [view, setView] = useState<'list' | 'chart'>('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Title | null>(null);
  const [reparenting, setReparenting] = useState<Title | null>(null);
  const [deleting, setDeleting] = useState<Title | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Server tree primary, flat `GetTitles` list fallback when `GetTree` fails. Tree key nests
  // under `['corporate','titles']` so the dialogs' existing invalidation refreshes both.
  const treeQuery = useQuery({
    queryKey: ['corporate', 'titles', 'tree'],
    queryFn: () => getTitleTree(),
    retry: false,
  });
  const listQuery = useQuery({
    queryKey: ['corporate', 'titles'],
    queryFn: () => getTitles(),
    enabled: treeQuery.isError,
  });
  const query = {
    isError: treeQuery.isError && listQuery.isError,
    error: listQuery.error ?? treeQuery.error,
    isFetching: treeQuery.isFetching || listQuery.isFetching,
  };
  const rows: Title[] = useMemo(
    () => (treeQuery.data?.tree ? treeToRows(treeQuery.data.tree) : listQuery.data?.titles ?? []),
    [treeQuery.data, listQuery.data]
  );

  const flattened = useMemo(() => flattenTree(rows), [rows]);
  const options = useMemo(
    () => flattened.map(({ row, depth }) => ({ id: row.id, label: `${'— '.repeat(depth)}${row.name}` })),
    [flattened]
  );
  const excludeIds = useMemo(
    () => (editing || reparenting ? collectDescendantIds(rows, (editing ?? reparenting)!.id) : new Set<number>()),
    [rows, editing, reparenting]
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTitle(id),
    onSuccess: () => {
      enqueueSnackbar('Unvan silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'titles'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Unvanlar</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Unvan
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <Tabs value={view} onChange={(_e, value) => setView(value)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab value="list" label="Liste" sx={{ minHeight: 36 }} />
        <Tab value="chart" label="Organizasyon Şeması" sx={{ minHeight: 36 }} />
      </Tabs>

      {view === 'chart' && (
        <Paper variant="outlined">
          <TitleOrgChartView rows={rows} onNodeClick={(title) => setEditing(title)} />
        </Paper>
      )}

      {view === 'list' && (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Unvan Adı</TableCell>
              <TableCell>Seviye</TableCell>
              <TableCell>Astları Görebilir</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {flattened.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı unvan bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {flattened.map(({ row, depth }) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ pl: 2 + depth * 3 }}>{row.name}</TableCell>
                <TableCell>{row.level}</TableCell>
                <TableCell>{row.canViewSubordinates ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>
                  <Chip size="small" color={row.isActive ? 'success' : 'default'} label={row.isActive ? 'Aktif' : 'Pasif'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Üst Unvanı Değiştir">
                    <IconButton size="small" onClick={() => setReparenting(row)} aria-label="Taşı">
                      <DriveFileMoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <TitleFormDialog
        open={createOpen}
        title={null}
        options={options}
        excludeIds={new Set()}
        onClose={() => setCreateOpen(false)}
      />
      <TitleFormDialog
        open={!!editing}
        title={editing}
        options={options}
        excludeIds={excludeIds}
        onClose={() => setEditing(null)}
      />
      <TitleReparentDialog
        open={!!reparenting}
        title={reparenting}
        options={options}
        excludeIds={excludeIds}
        onClose={() => setReparenting(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Unvanı sil"
        description={`"${deleting?.name ?? ''}" unvanını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
